import React from 'react';
import { X } from 'lucide-react';
// import { useDrag } from 'react-dnd';
import clsx from 'clsx';

interface TextPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddText: (type: 'heading' | 'subheading' | 'body') => void;
}

// Draggable Text Wrapper
function DraggableText({ type, children, onClick, className }: { type: 'heading' | 'subheading' | 'body', children: React.ReactNode, onClick: () => void, className?: string }) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('element-type', 'text-preset');
    e.dataTransfer.setData('text-type', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={clsx(className, 'opacity-100')}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function TextPanel({ isOpen, onClose, onAddText }: TextPanelProps) {
  return (
    <>
      {/* Backdrop */}


      {/* Panel */}
      <div
        className={`fixed left-[98px] top-[80px] w-[340px] h-[calc(100vh-100px)] bg-white rounded-2xl shadow-xl z-40 flex flex-col transition-opacity duration-300 ease-in-out border border-gray-100 overflow-hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between border-b border-gray-100 bg-white z-10">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">Text</h2>
            <p className="text-xs text-gray-500 mt-0.5">Add text to your design</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 custom-scrollbar">

          <div className="py-4 space-y-3">
            {/* Default Text Types */}
            <DraggableText
              type="heading"
              onClick={() => onAddText('heading')}
              className="w-full h-[60px] bg-gray-50 hover:bg-gray-100 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 transition-all flex items-center px-4 group cursor-pointer"
            >
              <span className="text-2xl font-bold text-gray-800 group-hover:text-black truncate">Add a heading</span>
            </DraggableText>

            <DraggableText
              type="subheading"
              onClick={() => onAddText('subheading')}
              className="w-full h-[50px] bg-gray-50 hover:bg-gray-100 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 transition-all flex items-center px-4 group cursor-pointer"
            >
              <span className="text-lg font-semibold text-gray-800 group-hover:text-black truncate">Add a subheading</span>
            </DraggableText>

            <DraggableText
              type="body"
              onClick={() => onAddText('body')}
              className="w-full h-[40px] bg-gray-50 hover:bg-gray-100 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 transition-all flex items-center px-4 group cursor-pointer"
            >
              <span className="text-sm text-gray-800 group-hover:text-black truncate">Add a little bit of body text</span>
            </DraggableText>
          </div>

          {/* Separator */}
          <div className="h-px bg-gray-100 my-2" />

          {/* Presets / Styles */}
          <div className="py-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Font Combinations</h3>
            <div className="grid grid-cols-2 gap-3">
              <DraggableText
                type="heading"
                onClick={() => onAddText('heading')}
                className="aspect-[3/2] bg-gray-50 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 hover:bg-gray-100 cursor-pointer flex flex-col items-center justify-center p-3 transition-all"
              >
                <div className="font-serif font-bold text-gray-900 text-lg leading-tight pointer-events-none">Classic</div>
                <div className="text-[10px] text-gray-500 mt-1 uppercase tracking-widest pointer-events-none">ELEGANT</div>
              </DraggableText>

              <DraggableText
                type="heading"
                onClick={() => onAddText('heading')}
                className="aspect-[3/2] bg-gray-50 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 hover:bg-gray-100 cursor-pointer flex flex-col items-center justify-center p-3 transition-all"
              >
                <div className="font-mono font-bold text-gray-900 text-lg leading-tight pointer-events-none">Mono</div>
                <div className="text-[10px] text-gray-500 mt-1 pointer-events-none">Modern & Raw</div>
              </DraggableText>

              <DraggableText
                type="heading"
                onClick={() => onAddText('heading')}
                className="aspect-[3/2] bg-gray-50 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 hover:bg-gray-100 cursor-pointer flex flex-col items-center justify-center p-3 transition-all"
              >
                <div className="font-sans font-black text-gray-900 text-xl leading-none tracking-tighter pointer-events-none">BOLD</div>
                <div className="text-[10px] text-gray-500 mt-1 font-bold pointer-events-none">IMPACT</div>
              </DraggableText>

              <DraggableText
                type="heading"
                onClick={() => onAddText('heading')}
                className="aspect-[3/2] bg-gray-50 rounded-lg border border-transparent hover:border-[#30A84B]/30 hover:ring-2 hover:ring-[#30A84B]/20 hover:bg-gray-100 cursor-pointer flex flex-col items-center justify-center p-3 transition-all"
              >
                <div className="italic font-light text-gray-900 text-xl leading-tight pointer-events-none">Soft</div>
                <div className="text-[10px] text-gray-500 mt-1 font-medium pointer-events-none">Minimalist</div>
              </DraggableText>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

